import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Plane, Luggage, Check, Info, X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import FlightPrice, { FlightPriceInline } from './FlightPrice';
import { formatFlightConnectionLabel } from '../../lib/flightConnection';
import { formatFlightDuration, arrivalDayOffset } from '../../lib/flightDuration';
import {
  isNoCheckedBaggageFeature,
  orderFareFeatures,
} from '../../lib/flightBaggage';

const FARE_CARD_THEMES = [
  { border: 'border-slate-200', header: 'bg-slate-100 text-slate-800' },
  { border: 'border-cyan-200', header: 'bg-cyan-50 text-cyan-900' },
  { border: 'border-violet-300', header: 'bg-violet-600 text-white' },
  { border: 'border-indigo-300', header: 'bg-indigo-900 text-white' },
];

function formatTime(dateString) {
  if (!dateString) return '';
  return format(new Date(dateString), 'HH:mm');
}

function BaggageExcludedIcon({ className = '' }) {
  return (
    <span className={`relative inline-flex shrink-0 ${className}`}>
      <Luggage className="w-4 h-4 text-gray-500" />
      <X
        className="w-3 h-3 text-red-600 absolute -top-1 -right-1.5 bg-white rounded-full"
        strokeWidth={3}
        aria-hidden
      />
    </span>
  );
}

function FeatureListIcon({ feat }) {
  if (isNoCheckedBaggageFeature(feat)) {
    return <BaggageExcludedIcon className="mt-0.5" />;
  }
  return <Check className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />;
}

export default function FlightFareGroupCard({ group, onSelectFare, formatPriceDelta }) {
  const fares = group?.fares || [];
  const [selectedOfferId, setSelectedOfferId] = useState(fares[0]?.offer_id || null);

  const selectedFare = useMemo(
    () => fares.find((f) => f.offer_id === selectedOfferId) || fares[0],
    [fares, selectedOfferId],
  );

  if (!group || fares.length === 0) return null;

  const dep = group.departure_airport;
  const arr = group.arrival_airport;
  const dayOffset = arrivalDayOffset(group.departure_time, group.arrival_time);
  const connectionLabel = formatFlightConnectionLabel(group);
  const hasConnection = Number(group.stop_count ?? 0) > 0;

  const handleSelect = () => {
    if (!selectedFare) return;
    const offer = {
      offer_id: selectedFare.offer_id,
      flight_id: group.flight_id,
      total_price: selectedFare.total_price,
      base_price: selectedFare.base_price,
      brand_name: selectedFare.brand_name,
      air_fare_code: selectedFare.air_fare_code,
      baggage_label: selectedFare.baggage_label,
      features: selectedFare.features,
      baggages: selectedFare.baggages,
    };
    onSelectFare(offer, group);
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`flight-group-${group.group_id}`}>
      <CardContent className="p-0">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-5 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
              <span className="text-base font-bold text-gray-600">{group.airline_code || 'XX'}</span>
            </div>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="text-center shrink-0">
                <div className="text-xl font-bold">{formatTime(group.departure_time)}</div>
                <div className="text-xs text-gray-500">{dep}</div>
              </div>
              <div className="flex-1 flex flex-col items-center min-w-[80px]">
                <div className="text-xs text-gray-500">{formatFlightDuration(group.travel_time)}</div>
                <div className="w-full h-px bg-gray-300 my-1 relative">
                  <Plane className="w-3.5 h-3.5 text-sky-600 absolute -top-1.5 left-1/2 -translate-x-1/2" />
                </div>
                <div className="text-xs text-gray-400 text-center leading-snug">
                  {connectionLabel}
                  {hasConnection ? (
                    <span className="block text-[10px] text-gray-400">toplam süre (bekleme dahil)</span>
                  ) : null}
                </div>
              </div>
              <div className="text-center shrink-0">
                <div className="text-xl font-bold">
                  {formatTime(group.arrival_time)}
                  {dayOffset > 0 ? (
                    <sup className="text-xs text-amber-600 ml-0.5" title="Varış ertesi gün">
                      +{dayOffset}
                    </sup>
                  ) : null}
                </div>
                {dayOffset > 0 ? (
                  <div className="text-[10px] text-amber-600">ertesi gün</div>
                ) : null}
                <div className="text-xs text-gray-500">{arr}</div>
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-gray-500">En uygun fiyat</div>
            <FlightPrice amountEur={group.min_total_price} size="lg" className="items-end" />
          </div>
        </div>

        <div className="p-4 bg-slate-50/80">
          <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory">
            {fares.map((fare, idx) => {
              const theme = FARE_CARD_THEMES[idx % FARE_CARD_THEMES.length];
              const isSelected = selectedFare?.offer_id === fare.offer_id;
              const isRecommended = fare.recommended;
              const hasNoCheckedBaggage = Number(fare.baggage ?? 0) === 0;
              const displayFeatures = orderFareFeatures(fare.features, hasNoCheckedBaggage).slice(0, 4);
              return (
                <button
                  key={fare.offer_id}
                  type="button"
                  onClick={() => setSelectedOfferId(fare.offer_id)}
                  className={`snap-start shrink-0 w-[200px] sm:w-[220px] rounded-xl border-2 text-left transition-all ${theme.border} ${
                    isSelected ? 'ring-2 ring-sky-500 ring-offset-2 shadow-md' : 'opacity-90 hover:opacity-100'
                  }`}
                  data-testid={`fare-package-${fare.offer_id}`}
                >
                  <div className={`px-3 py-2 rounded-t-[10px] text-sm font-semibold relative ${theme.header}`}>
                    {fare.brand_name}
                    {isRecommended && (
                      <span className="absolute -top-2 right-2 text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-medium">
                        Önerilen
                      </span>
                    )}
                  </div>
                  <div className="bg-white px-3 py-3 rounded-b-[10px] min-h-[140px] flex flex-col">
                    <div className="text-lg font-bold text-gray-900">
                      {fare.price_delta > 0 ? (
                        <span className="text-sm font-normal text-gray-600 block">+ yolcu başına</span>
                      ) : null}
                      {fare.price_delta > 0 ? (
                        <FlightPrice amountEur={fare.price_delta} size="default" />
                      ) : (
                        <FlightPrice amountEur={fare.total_price} size="default" />
                      )}
                    </div>
                    {fare.price_delta === 0 && (
                      <p className="text-[11px] text-gray-500 mb-2">Toplam fiyat</p>
                    )}
                    <ul className="space-y-1.5 mt-auto">
                      {displayFeatures.map((feat) => (
                        <li key={feat} className="flex items-start gap-1.5 text-[11px] text-gray-600 leading-snug">
                          <FeatureListIcon feat={feat} />
                          <span>{feat}</span>
                        </li>
                      ))}
                      {displayFeatures.length === 0 && fare.baggage_label && (
                        <li className="flex items-start gap-1.5 text-[11px] text-gray-600">
                          {hasNoCheckedBaggage ? (
                            <BaggageExcludedIcon className="mt-0.5" />
                          ) : (
                            <Luggage className="w-3 h-3 text-sky-600 shrink-0 mt-0.5" />
                          )}
                          <span>{fare.baggage_label}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedFare?.price_delta === 0 && selectedFare?.baggage === 0 && selectedFare?.handLuggage > 0 && (
            <div className="mt-3 flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Kabin ve kayıtlı bagaj ek ücretle seçim aşamasında eklenebilir.</span>
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              Seçili paket:{' '}
              <span className="font-semibold text-gray-900">{selectedFare?.brand_name}</span>
              {selectedFare?.baggage_label ? (
                <span className="text-gray-500"> · {selectedFare.baggage_label}</span>
              ) : null}
            </div>
            <Button
              className="sm:min-w-[160px]"
              onClick={handleSelect}
              data-testid={`select-fare-${selectedFare?.offer_id}`}
            >
              Seç — <FlightPriceInline amountEur={selectedFare?.total_price} className="font-semibold" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
